import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { CheckCircle, Upload, Search, Loader2, FileText, ImageIcon, X, FileType2, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight, Paperclip, ExternalLink, Wallet, Zap, Clock, AlertTriangle } from 'lucide-react';
import { useDataCache } from '@/hooks/useDataCache';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { getMexicoTodayDateOnly, parseLocalDate } from '@/lib/utils';
import { SerieFolioSelector, type AvailableSeries } from '@/components/treasury/SerieFolioSelector';

const ALL_STATUSES = [
  { value: 'aspirante', label: 'Aspirante' },
  { value: 'aceptado', label: 'Aceptado' },
  { value: 'nuevo', label: 'Nuevo' },
  { value: 'activo', label: 'Activo' },
  { value: 'egresado', label: 'Egresado' },
  { value: 'baja', label: 'Baja' },
  { value: 'baja_temporal', label: 'Baja Temporal' },
  { value: 'baja_definitiva', label: 'Baja Definitiva' },
  { value: 'baja_administrativa', label: 'Baja Administrativa' },
  { value: 'baja_voluntaria', label: 'Baja Voluntaria' },
  { value: 'baja_por_inasistencia', label: 'Baja por Inasistencia' },
  { value: 'reincorporado', label: 'Reincorporado' },
  { value: 'en_espera', label: 'En Espera' },
];

const STATUS_COLORS: Record<string, string> = {
  aspirante: 'bg-yellow-100 text-yellow-800',
  aceptado: 'bg-green-100 text-green-800',
  nuevo: 'bg-blue-100 text-blue-800',
  activo: 'bg-emerald-100 text-emerald-800',
  egresado: 'bg-purple-100 text-purple-800',
  baja: 'bg-red-100 text-red-800',
  baja_temporal: 'bg-orange-100 text-orange-800',
  baja_definitiva: 'bg-red-200 text-red-900',
  baja_administrativa: 'bg-red-100 text-red-800',
  baja_voluntaria: 'bg-orange-100 text-orange-800',
  baja_por_inasistencia: 'bg-orange-200 text-orange-900',
  reincorporado: 'bg-teal-100 text-teal-800',
  en_espera: 'bg-gray-100 text-gray-800',
};

interface Alumno {
  id: number;
  numeroIdentificacionUnico: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  email: string;
  estatus: string;
  numeroReferenciaExterna: string | null;
  fechaCreacion: string;
  pendingInvoicesCount: number;
  groupName?: string | null;
  groupCode?: string | null;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface AspirantesResponse {
  data: Alumno[];
  pagination: PaginationInfo;
}

interface PaymentProduct {
  id: number;
  name: string;
  sku: string;
}

interface PendingInvoice {
  id: number;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  total: string;
  pendingAmount: string;
  allowedMethods: { value: string; label: string }[];
}

interface PaymentAccount {
  id: number;
  code: string;
  name: string;
}

interface TuitionPricing {
  installmentNumber: number;
  planId: number;
  baseAmount: number;
  discountAmount: number;
  surchargeAmount: number;
  dueDate: string;
  graceDeadline: string;
}

interface PaymentProof {
  id: number;
  alumnoId: number;
  archivoUrl: string;
  archivoNombre: string | null;
  archivoTipo: string | null;
  referencia: string | null;
  status: 'pendiente' | 'validado' | 'rechazado';
  createdAt: string;
}

export default function PaymentValidation() {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');

  // Dialog state
  const [selectedAlumno, setSelectedAlumno] = useState<Alumno | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [bulkBankAccountId, setBulkBankAccountId] = useState<string | null>(null);

  // Payment form fields
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [manualAmount, setManualAmount] = useState('');
  const [fechaPago, setFechaPago] = useState(getMexicoTodayDateOnly());
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [bankAccountId, setBankAccountId] = useState<string | null>(null);
  const [referencia, setReferencia] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentImage, setPaymentImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [tuitionPricing, setTuitionPricing] = useState<TuitionPricing | null>(null);
  const [isFetchingPricing, setIsFetchingPricing] = useState(false);
  const [manualSerieId, setManualSerieId] = useState<number | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { invalidateCache } = useDataCache();

  // Ticket 7: same folio-series control cuentas de cobro already has — keyed by student
  // (not invoice) since this screen also supports waterfall mode across all pending invoices.
  const { data: availableSeries } = useQuery<AvailableSeries>({
    queryKey: ['available-series-alumno', selectedAlumno?.id],
    queryFn: async () => {
      const res = await fetch(`/api/treasury/alumnos/${selectedAlumno!.id}/available-series`, { credentials: 'include' });
      if (!res.ok) return { invoicingSettingsId: null, razonSocialName: null, series: [], defaults: [] };
      return res.json();
    },
    enabled: !!selectedAlumno && showPaymentDialog,
    staleTime: 60 * 1000,
  });

  const autoSerieId = availableSeries?.defaults.find(d => d.paymentMethod === paymentMethod)?.seriesId ?? null;
  const effectiveSerieId = manualSerieId ?? autoSerieId;

  useEffect(() => {
    setManualSerieId(null); // reset manual override when payment method changes, so autoSerieId takes over again
  }, [paymentMethod]);

  // Fetch tuition pricing when a specific invoice is selected
  useEffect(() => {
    if (!selectedInvoiceId) {
      setTuitionPricing(null);
      return;
    }
    setIsFetchingPricing(true);
    fetch(`/api/treasury/invoices/${selectedInvoiceId}/tuition-pricing`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => setTuitionPricing(data ?? null))
      .catch(() => setTuitionPricing(null))
      .finally(() => setIsFetchingPricing(false));
  }, [selectedInvoiceId]);

  // Products for filter dropdown
  const { data: paymentProducts } = useQuery<PaymentProduct[]>({
    queryKey: ['payment-products'],
    queryFn: async () => {
      const res = await fetch('/api/administration/payment-products', { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar productos');
      return res.json();
    },
  });

  // Main list
  const { data, isLoading, refetch } = useQuery<AspirantesResponse>({
    queryKey: ['payment-validation', currentPage, pageSize, searchTerm, statusFilter, productFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        search: searchTerm,
        estatus: statusFilter,
        productId: productFilter,
      });
      const res = await fetch(`/api/administration/alumnos-aspirantes?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar alumnos');
      return res.json();
    },
  });

  // Pending invoices for selected alumno (loaded when dialog opens)
  const { data: pendingInvoices, isLoading: invoicesLoading } = useQuery<PendingInvoice[]>({
    queryKey: ['alumno-pending-invoices', selectedAlumno?.id],
    queryFn: async () => {
      const res = await fetch(`/api/administration/alumno-pending-invoices/${selectedAlumno!.id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar facturas');
      return res.json();
    },
    enabled: !!selectedAlumno && showPaymentDialog,
  });

  // ASSET accounts for cuenta de destino
  const { data: paymentAccounts } = useQuery<PaymentAccount[]>({
    queryKey: ['payment-accounts'],
    queryFn: async () => {
      const res = await fetch('/api/administration/payment-accounts', { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar cuentas');
      return res.json();
    },
  });

  // All pending payment proofs (for list indicators)
  const { data: allPendingProofs = [] } = useQuery<PaymentProof[]>({
    queryKey: ['payment-proofs-pending-all'],
    queryFn: async () => {
      const res = await fetch('/api/payment-proofs?status=pendiente', { credentials: 'include' });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 30000,
  });

  // Pending proofs for the selected alumno in the dialog
  const { data: alumnoProofs = [], isLoading: proofLoading } = useQuery<PaymentProof[]>({
    queryKey: ['alumno-payment-proofs', selectedAlumno?.id],
    queryFn: async () => {
      const res = await fetch(`/api/payment-proofs?alumnoId=${selectedAlumno!.id}&status=pendiente`, { credentials: 'include' });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedAlumno && showPaymentDialog,
  });

  // Map of alumnoId → pending proof count
  const proofCountByAlumno = allPendingProofs.reduce<Record<number, number>>((acc, p) => {
    acc[p.alumnoId] = (acc[p.alumnoId] || 0) + 1;
    return acc;
  }, {});

  // Saldos a Favor — fetch active credits to show balance indicators per student
  const { data: activeCredits = [] } = useQuery<Array<{ alumnoId: number; amountRemaining: string }>>({
    queryKey: ['student-credits-active-map'],
    queryFn: async () => {
      const res = await fetch('/api/treasury/student-credits?status=active&limit=1000', { credentials: 'include' });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Build alumnoId → total remaining credit balance
  const creditBalanceByAlumno = activeCredits.reduce<Record<number, number>>((acc, c) => {
    acc[c.alumnoId] = (acc[c.alumnoId] || 0) + parseFloat(c.amountRemaining || '0');
    return acc;
  }, {});

  // Credit balance for the currently selected alumno in the payment dialog
  const selectedAlumnoCreditBalance = selectedAlumno ? (creditBalanceByAlumno[selectedAlumno.id] || 0) : 0;

  // Derived: selected invoice object
  const selectedInvoice = pendingInvoices?.find(i => i.id.toString() === selectedInvoiceId) ?? null;

  // Compute IVA-inclusive pronto-pago / recargo amounts using the invoice total as the tax reference
  const tuitionPricingWithTax = (() => {
    if (!tuitionPricing || !selectedInvoice) return null;
    const invoiceTotal = parseFloat(selectedInvoice.total);
    const pendingAmount = parseFloat(selectedInvoice.pendingAmount);
    const base = tuitionPricing.baseAmount;
    if (!base || base <= 0) return null;
    const taxRatio = invoiceTotal / base;
    // El recargo se prorratea sobre el saldo real pendiente, no sobre la cuota completa —
    // mismo criterio que treasury.service.ts registerPayment().
    const surchargePct = tuitionPricing.surchargeAmount / base - 1;
    return {
      discountWithTax: Math.round(tuitionPricing.discountAmount * taxRatio * 100) / 100,
      baseWithTax: invoiceTotal,
      surchargeWithTax: Math.round(pendingAmount * (1 + surchargePct) * 100) / 100,
      dueDate: tuitionPricing.dueDate,
      graceDeadline: tuitionPricing.graceDeadline,
    };
  })();

  // Determine which pricing scenario is active based on the current fechaPago
  // 'A' = Pronto Pago, 'B' = Normal (grace period), 'C' = Recargo (mora)
  const activeScenario = (() => {
    if (!tuitionPricingWithTax) return null;
    const pd = parseLocalDate(fechaPago);
    const dd = parseLocalDate(tuitionPricingWithTax.dueDate);
    const gd = parseLocalDate(tuitionPricingWithTax.graceDeadline);
    if (pd <= dd) return 'A';
    if (pd > gd) return 'C';
    return 'B';
  })();

  // Effective cap based on the selected payment date vs. tuition plan deadlines
  // Mirrors the backend effectiveCap logic so the UI can warn before submitting
  const frontendEffectiveCap = (() => {
    if (!selectedInvoice) return null;
    const base = parseFloat(selectedInvoice.pendingAmount);
    if (!tuitionPricingWithTax) return base;
    if (activeScenario === 'A') return tuitionPricingWithTax.discountWithTax;
    if (activeScenario === 'C') return tuitionPricingWithTax.surchargeWithTax;
    return tuitionPricingWithTax.baseWithTax;
  })();

  // Auto-fill the correct amount whenever the invoice or payment date changes.
  // This prevents the user from accidentally submitting a pronto-pago amount
  // on a mora-period date (which would leave a pending balance).
  useEffect(() => {
    if (!tuitionPricingWithTax || !selectedInvoiceId) return;
    const pd = parseLocalDate(fechaPago);
    const dd = parseLocalDate(tuitionPricingWithTax.dueDate);
    const gd = parseLocalDate(tuitionPricingWithTax.graceDeadline);
    if (pd <= dd) {
      setManualAmount(tuitionPricingWithTax.discountWithTax.toFixed(2));
    } else if (pd > gd) {
      setManualAmount(tuitionPricingWithTax.surchargeWithTax.toFixed(2));
    } else {
      setManualAmount(tuitionPricingWithTax.baseWithTax.toFixed(2));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoiceId, fechaPago, tuitionPricingWithTax?.dueDate, tuitionPricingWithTax?.graceDeadline]);

  // Available payment methods: from selected invoice, or union of all pending invoices
  const availablePaymentMethods: { value: string; label: string }[] = (() => {
    const ALL_METHODS = [
      { value: 'TRANSFER', label: 'Transferencia' },
      { value: 'CHECK', label: 'Cheque' },
      { value: 'CASH', label: 'Efectivo' },
      { value: 'CARD', label: 'Tarjeta' },
      { value: 'OTHER', label: 'Otro' },
    ];
    if (selectedInvoice) return selectedInvoice.allowedMethods;
    if (!pendingInvoices || pendingInvoices.length === 0) return ALL_METHODS;
    const seen = new Map<string, string>();
    pendingInvoices.forEach(inv =>
      inv.allowedMethods.forEach(m => { if (!seen.has(m.value)) seen.set(m.value, m.label); })
    );
    return seen.size > 0 ? Array.from(seen, ([value, label]) => ({ value, label })) : ALL_METHODS;
  })();


  // Payment mutation using FormData (supports image)
  const validateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedAlumno || !fechaPago || !paymentMethod || !bankAccountId) {
        throw new Error('Por favor completa todos los campos requeridos');
      }
      if (!manualAmount || parseFloat(manualAmount) <= 0) {
        throw new Error('Ingresa el monto a pagar');
      }
      const formData = new FormData();
      formData.append('alumnoId', selectedAlumno.id.toString());
      formData.append('amount', manualAmount);
      if (selectedInvoiceId) {
        formData.append('invoiceId', selectedInvoiceId);
      }
      formData.append('fechaPago', fechaPago);
      formData.append('paymentMethod', paymentMethod);
      formData.append('bankAccountId', bankAccountId);
      if (effectiveSerieId) formData.append('serieId', String(effectiveSerieId));
      if (referencia) formData.append('referencia', referencia);
      if (notes) formData.append('notes', notes);
      if (paymentImage) formData.append('image', paymentImage);

      const res = await fetch('/api/administration/validate-payment', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al registrar el pago');
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      let desc = data.mode === 'waterfall'
        ? `$${data.totalApplied.toFixed(2)} aplicado a ${data.applied.length} factura${data.applied.length !== 1 ? 's' : ''}`
        : 'El pago se registró y aplicó a la cuenta de cobro exitosamente';
      if (data.autoCredit) {
        desc += ` · Saldo a favor creado: $${data.autoCredit.amount.toFixed(2)}`;
      } else if (data.mode === 'waterfall' && data.remaining > 0) {
        desc += ` · Sobrante: $${data.remaining.toFixed(2)}`;
      }
      toast({ title: 'Pago Registrado', description: desc });
      queryClient.invalidateQueries({ queryKey: ['payment-validation'] });
      queryClient.invalidateQueries({ queryKey: ['payment-products'] });
      queryClient.invalidateQueries({ queryKey: ['alumnos'] });
      queryClient.invalidateQueries({ queryKey: ['payment-proofs-pending-all'] });
      queryClient.invalidateQueries({ queryKey: ['student-credits-active-map'] });
      invalidateCache('alumnos');
      closePaymentDialog();
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Bulk CSV mutation — parses CSV client-side, POSTs JSON to /api/treasury/payments/bulk-csv
  const bulkValidateMutation = useMutation({
    mutationFn: async ({ file, bankAccId }: { file: File; bankAccId: number }) => {
      // Parse CSV text
      const text = await file.text();
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) throw new Error('El CSV debe tener encabezado y al menos una fila de datos');

      const sep = lines[0].includes(';') ? ';' : ',';
      const headers = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/[^a-záéíóúñ0-9_]/gi, ''));

      const idxRef  = headers.findIndex(h => h.includes('referencia') || h === 'ref');
      const idxConc = headers.findIndex(h => h.includes('concepto') || h.includes('descripcion') || h.includes('detalle') || h.includes('nota'));
      const idxCant = headers.findIndex(h => h.includes('cantidad') || h.includes('monto') || h.includes('importe') || h.includes('pago'));
      const idxFech = headers.findIndex(h => h.includes('fecha') || h.includes('date'));

      if (idxRef  < 0) throw new Error('Columna "referencia" no encontrada');
      if (idxCant < 0) throw new Error('Columna "cantidad" no encontrada');
      if (idxFech < 0) throw new Error('Columna "fecha" no encontrada');

      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(sep).map(c => c.trim().replace(/^"|"$/g, ''));
        const referencia = cols[idxRef] || '';
        const concepto   = idxConc >= 0 ? (cols[idxConc] || '') : '';
        const cantidadRaw = (cols[idxCant] || '0').replace(/[$,\s]/g, '');
        const cantidad   = parseFloat(cantidadRaw);
        const fecha      = cols[idxFech] || '';
        if (!referencia || isNaN(cantidad) || cantidad <= 0 || !fecha) continue;
        rows.push({ referencia, concepto, cantidad, fecha });
      }

      if (rows.length === 0) throw new Error('No se encontraron filas válidas en el CSV');

      const res = await fetch('/api/treasury/payments/bulk-csv', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bankAccountId: bankAccId, rows }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Error al procesar pagos masivos');
      }
      return res.json();
    },
    onSuccess: (data) => {
      const results: any[] = data.results || [];
      const ok       = results.filter(r => r.status === 'ok').length;
      const errores  = results.filter(r => r.status === 'error').length;
      const sinFact  = results.filter(r => r.status === 'sin_facturas').length;
      const parcial  = results.filter(r => r.status === 'parcial').length;
      const creditos = results.filter(r => r.autoCreditId).length;
      const creditFailed = results.filter(r => r.status === 'ok' && !r.autoCreditId && r.message?.includes('Sobrante') && r.message?.includes('no pudo guardarse')).length;
      let description = `✅ ${ok} registrados${creditos > 0 ? ` (${creditos} con saldo a favor)` : ''} · ⚠️ ${sinFact} sin facturas · ❌ ${errores} errores — de ${results.length} filas`;
      if (parcial > 0) description += ` · ⚡ ${parcial} con sobrante pendiente`;
      if (creditFailed > 0) description += ` · ⚠️ ${creditFailed} saldo a favor no guardado`;
      toast({
        title: 'Procesamiento completado',
        description,
      });
      queryClient.invalidateQueries({ queryKey: ['payment-validation'] });
      queryClient.invalidateQueries({ queryKey: ['alumnos'] });
      queryClient.invalidateQueries({ queryKey: ['student-credits-active-map'] });
      invalidateCache('alumnos');
      setShowBulkDialog(false);
      setSelectedFile(null);
      setBulkBankAccountId(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Error al procesar CSV', description: error.message, variant: 'destructive' });
    },
  });

  const openPaymentDialog = (alumno: Alumno) => {
    setSelectedAlumno(alumno);
    setSelectedInvoiceId(null);
    setManualAmount('');
    setFechaPago(new Date().toISOString().split('T')[0]);
    setPaymentMethod(null);
    setBankAccountId(null);
    setReferencia('');
    setNotes('');
    setPaymentImage(null);
    setImagePreview(null);
    setManualSerieId(null);
    setShowPaymentDialog(true);
  };

  const closePaymentDialog = () => {
    setShowPaymentDialog(false);
    setSelectedAlumno(null);
    setSelectedInvoiceId(null);
    setManualAmount('');
    setPaymentImage(null);
    setImagePreview(null);
    setTuitionPricing(null);
    setManualSerieId(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPaymentImage(file);
    if (file.type === 'application/pdf') {
      setImagePreview('pdf');
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });

  const formatCurrency = (amount: string | number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(amount));

  const getStatusLabel = (value: string) => {
    if (!value) return 'Todos los estatus';
    return ALL_STATUSES.find(s => s.value === value)?.label ?? value;
  };

  const isFormValid = !!manualAmount && parseFloat(manualAmount) > 0 && !!fechaPago && !!paymentMethod && !!bankAccountId && !!effectiveSerieId;

  return (
    <div className="space-y-4">
      {/* Filters bar */}
      <div className="flex justify-between items-center gap-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, NUI, email..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="pl-8"
            />
          </div>
          <SearchableSelect
            options={ALL_STATUSES}
            value={statusFilter || null}
            onChange={(val) => { setStatusFilter(val ? String(val) : ''); setCurrentPage(1); }}
            placeholder="Todos los estatus"
            className="w-48"
          />
          <SearchableSelect
            options={(paymentProducts ?? []).map(p => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
            value={productFilter || null}
            onChange={(val) => { setProductFilter(val ? String(val) : ''); setCurrentPage(1); }}
            placeholder="Todos los productos"
            className="w-56"
          />
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {data && (
        <p className="text-sm text-muted-foreground">
          {data.pagination.total} cliente{data.pagination.total !== 1 ? 's' : ''} con pagos pendientes
          {statusFilter ? ` · Estatus: ${getStatusLabel(statusFilter)}` : ''}
          {productFilter && paymentProducts
            ? ` · Producto: ${paymentProducts.find(p => p.id.toString() === productFilter)?.name ?? ''}`
            : ''}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : (
        <>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>NUI</TableHead>
                  <TableHead>Nombre Completo</TableHead>
                  <TableHead>Grupo</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Estatus</TableHead>
                  <TableHead className="text-center">Facturas Pendientes</TableHead>
                  <TableHead>Fecha Creación</TableHead>
                  <TableHead className="text-center">Comprobante</TableHead>
                  <TableHead className="text-center">Saldo a Favor</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.data && data.data.length > 0 ? (
                  data.data.map((alumno) => (
                    <TableRow key={alumno.id}>
                      <TableCell className="font-medium">{alumno.numeroIdentificacionUnico}</TableCell>
                      <TableCell>{`${alumno.nombre} ${alumno.apellidoPaterno} ${alumno.apellidoMaterno || ''}`}</TableCell>
                      <TableCell>
                        {alumno.groupName ? (
                          <div className="flex flex-col">
                            <span className="text-sm font-medium leading-tight">{alumno.groupName}</span>
                            {alumno.groupCode && (
                              <span className="text-xs text-muted-foreground">{alumno.groupCode}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>{alumno.email || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[alumno.estatus] ?? 'bg-gray-100 text-gray-800'}`}>
                          {getStatusLabel(alumno.estatus)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center gap-1 text-orange-600 font-semibold">
                          <FileText className="h-3.5 w-3.5" />
                          {alumno.pendingInvoicesCount}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(alumno.fechaCreacion)}</TableCell>
                      <TableCell className="text-center">
                        {proofCountByAlumno[alumno.id] ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-300">
                            <Paperclip className="h-3 w-3" />
                            {proofCountByAlumno[alumno.id]}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {(creditBalanceByAlumno[alumno.id] ?? 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 border border-violet-300">
                            <Wallet className="h-3 w-3" />
                            {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(creditBalanceByAlumno[alumno.id])}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" onClick={() => openPaymentDialog(alumno)}>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Registrar Pago
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No hay clientes con pagos pendientes
                      {statusFilter ? ` con estatus "${getStatusLabel(statusFilter)}"` : ''}
                      {searchTerm ? ` que coincidan con "${searchTerm}"` : ''}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* ── Pagination bar ─────────────────────────────── */}
          {data?.pagination && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
              {/* Left: info + page size */}
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span>
                  Mostrando{' '}
                  <strong className="text-foreground">
                    {data.pagination.total === 0
                      ? 0
                      : (currentPage - 1) * pageSize + 1}
                    –{Math.min(currentPage * pageSize, data.pagination.total)}
                  </strong>{' '}
                  de{' '}
                  <strong className="text-foreground">{data.pagination.total}</strong>{' '}
                  cliente{data.pagination.total !== 1 ? 's' : ''}
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs">Por página:</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}
                  >
                    <SelectTrigger className="h-7 w-[70px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 25, 50, 100].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Right: page buttons */}
              {data.pagination.totalPages > 1 && (
                <div className="flex items-center gap-1">
                  {/* First */}
                  <Button
                    variant="outline" size="icon" className="h-8 w-8"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    title="Primera página"
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  {/* Prev */}
                  <Button
                    variant="outline" size="icon" className="h-8 w-8"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    title="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {/* Page number buttons */}
                  {(() => {
                    const total = data.pagination.totalPages;
                    const delta = 2;
                    const pages: (number | '...')[] = [];
                    for (let i = 1; i <= total; i++) {
                      if (i === 1 || i === total || (i >= currentPage - delta && i <= currentPage + delta)) {
                        pages.push(i);
                      } else if (
                        (i === currentPage - delta - 1 && i > 1) ||
                        (i === currentPage + delta + 1 && i < total)
                      ) {
                        pages.push('...');
                      }
                    }
                    return pages.map((p, idx) =>
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-1 text-muted-foreground text-sm">…</span>
                      ) : (
                        <Button
                          key={p}
                          variant={p === currentPage ? 'default' : 'outline'}
                          size="icon"
                          className="h-8 w-8 text-xs"
                          onClick={() => setCurrentPage(p as number)}
                        >
                          {p}
                        </Button>
                      )
                    );
                  })()}

                  {/* Next */}
                  <Button
                    variant="outline" size="icon" className="h-8 w-8"
                    onClick={() => setCurrentPage(p => Math.min(data.pagination.totalPages, p + 1))}
                    disabled={currentPage === data.pagination.totalPages}
                    title="Página siguiente"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  {/* Last */}
                  <Button
                    variant="outline" size="icon" className="h-8 w-8"
                    onClick={() => setCurrentPage(data.pagination.totalPages)}
                    disabled={currentPage === data.pagination.totalPages}
                    title="Última página"
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Payment Dialog ────────────────────────────────────── */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => { if (!open) closePaymentDialog(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Registrar Pago</DialogTitle>
            <DialogDescription>
              Captura los datos del pago para{' '}
              <strong>{selectedAlumno ? `${selectedAlumno.nombre} ${selectedAlumno.apellidoPaterno}` : ''}</strong>
              {' '}· {selectedAlumno?.numeroIdentificacionUnico}
            </DialogDescription>
          </DialogHeader>

          {selectedAlumnoCreditBalance > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-50 border border-violet-200 text-sm">
              <Wallet className="h-4 w-4 text-violet-600 shrink-0" />
              <span className="text-violet-800">
                Este alumno tiene un <strong>saldo a favor de{' '}
                {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(selectedAlumnoCreditBalance)}
                </strong> disponible. Puedes aplicarlo desde{' '}
                <a href="/app/treasury/creditos" target="_blank" rel="noreferrer" className="underline font-medium">Saldos a Favor</a>.
              </span>
            </div>
          )}

          {invoicesLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <div className="space-y-4 py-2 overflow-y-auto max-h-[60vh] pr-1">

              {/* ── Comprobantes adjuntos por el alumno ── */}
              {proofLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verificando comprobantes...
                </div>
              ) : alumnoProofs.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                  <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5" />
                    {alumnoProofs.length === 1 ? 'Comprobante adjunto por el alumno' : `${alumnoProofs.length} comprobantes adjuntos por el alumno`}
                  </p>
                  {alumnoProofs.map((proof) => {
                    const isPdf = proof.archivoTipo === 'application/pdf' || proof.archivoNombre?.toLowerCase().endsWith('.pdf');
                    const fileUrl = `/api/payment-proofs/${proof.id}/file`;
                    return (
                      <div key={proof.id} className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs text-amber-600">
                          <span className="font-medium truncate">{proof.archivoNombre || 'Comprobante'}</span>
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-amber-700 hover:text-amber-900 underline shrink-0 ml-2"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Abrir
                          </a>
                        </div>
                        {isPdf ? (
                          <div className="flex items-center gap-2 bg-white border border-amber-200 rounded px-3 py-2">
                            <FileType2 className="h-6 w-6 text-red-500 shrink-0" />
                            <span className="text-xs text-gray-600">Documento PDF</span>
                          </div>
                        ) : (
                          <img
                            src={fileUrl}
                            alt="Comprobante de pago"
                            className="w-full max-h-48 object-contain rounded border border-amber-200 bg-white cursor-pointer"
                            onClick={() => window.open(fileUrl, '_blank')}
                          />
                        )}
                        {proof.referencia && (
                          <p className="text-xs text-amber-600">Referencia: {proof.referencia}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Notice: pending proof but no invoices */}
              {!invoicesLoading && alumnoProofs.length > 0 && (!pendingInvoices || pendingInvoices.length === 0) && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm">
                  <span className="text-amber-600 mt-0.5">⚠️</span>
                  <span className="text-amber-800">
                    Este alumno tiene un comprobante adjunto pero <strong>no tiene facturas pendientes</strong>.
                    Genera una factura de inscripción o colegiatura desde{' '}
                    <a href="/app/sales/invoices" target="_blank" rel="noreferrer" className="underline font-medium">Ventas → Facturas</a>{' '}
                    antes de registrar el pago.
                  </span>
                </div>
              )}

              {/* Cuenta de cobro (opcional) */}
              <div className="space-y-1.5">
                <Label>
                  Cargo específico{' '}
                  <span className="text-muted-foreground text-xs font-normal">(opcional — si no seleccionas, el sistema aplica por fecha de vencimiento)</span>
                </Label>
                <SearchableSelect
                  options={(pendingInvoices ?? []).map(inv => ({
                    value: inv.id,
                    label: `${inv.invoiceNumber} — ${formatCurrency(inv.pendingAmount)} pendiente · vence ${formatDate(inv.dueDate)}`,
                  }))}
                  value={selectedInvoiceId ? parseInt(selectedInvoiceId) : null}
                  onChange={(val) => {
                    const inv = val ? (pendingInvoices ?? []).find(i => i.id === Number(val)) : null;
                    setSelectedInvoiceId(val ? String(val) : null);
                    setManualAmount(inv ? inv.pendingAmount : '');
                    setPaymentMethod(null);
                    setTuitionPricing(null);
                  }}
                  placeholder="Seleccionar cargo (dejar vacío para cascada automática)..."
                  className="w-full"
                />
                {selectedInvoice && (
                  <p className="text-xs text-muted-foreground">
                    Total factura: {formatCurrency(selectedInvoice.total)} · Vence: {formatDate(selectedInvoice.dueDate)}
                  </p>
                )}
              </div>

              {/* Opciones de precio de colegiatura (Pronto Pago / Normal / Recargo) */}
              {selectedInvoiceId && (
                isFetchingPricing ? (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground py-1">
                    <Loader2 className="h-3 w-3 animate-spin" />Cargando tarifas de colegiatura...
                  </div>
                ) : tuitionPricingWithTax ? (
                  <div className={`rounded-lg border p-3 space-y-2 ${
                    activeScenario === 'C' ? 'border-orange-200 bg-orange-50' :
                    activeScenario === 'A' ? 'border-green-200 bg-green-50' :
                    'border-blue-100 bg-blue-50'
                  }`}>
                    <p className={`text-xs font-semibold ${
                      activeScenario === 'C' ? 'text-orange-700' :
                      activeScenario === 'A' ? 'text-green-700' :
                      'text-blue-700'
                    }`}>
                      Tarifas de colegiatura #{tuitionPricing!.installmentNumber}
                      {activeScenario === 'A' && ' · ✓ En periodo de pronto pago'}
                      {activeScenario === 'B' && ' · ✓ En periodo de gracia'}
                      {activeScenario === 'C' && ' · ⚠ En periodo de mora'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setManualAmount(tuitionPricingWithTax.discountWithTax.toFixed(2))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                          activeScenario === 'A'
                            ? 'bg-green-200 text-green-900 border-green-400 ring-2 ring-green-300 font-semibold'
                            : 'bg-gray-50 text-gray-400 border-gray-200 opacity-50 cursor-not-allowed'
                        }`}
                        title={activeScenario === 'A'
                          ? `Pronto pago activo — aplica antes del ${formatDate(tuitionPricingWithTax.dueDate)}`
                          : `No aplica — la fecha de pago ya pasó el ${formatDate(tuitionPricingWithTax.dueDate)}`}
                        disabled={activeScenario !== 'A'}
                      >
                        <Zap className="h-3 w-3" />
                        Pronto Pago: {formatCurrency(tuitionPricingWithTax.discountWithTax)}
                      </button>
                      <button
                        type="button"
                        onClick={() => setManualAmount(tuitionPricingWithTax.baseWithTax.toFixed(2))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                          activeScenario === 'B'
                            ? 'bg-blue-200 text-blue-900 border-blue-400 ring-2 ring-blue-300 font-semibold'
                            : 'bg-gray-50 text-gray-400 border-gray-200 opacity-50 cursor-not-allowed'
                        }`}
                        title={activeScenario === 'B'
                          ? 'Precio normal activo — dentro del periodo de gracia'
                          : activeScenario === 'A'
                            ? 'No aplica todavía — aún en periodo de pronto pago'
                            : 'No aplica — la fecha de pago ya superó el periodo de gracia'}
                        disabled={activeScenario !== 'B'}
                      >
                        <Clock className="h-3 w-3" />
                        Normal: {formatCurrency(tuitionPricingWithTax.baseWithTax)}
                      </button>
                      <button
                        type="button"
                        onClick={() => setManualAmount(tuitionPricingWithTax.surchargeWithTax.toFixed(2))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                          activeScenario === 'C'
                            ? 'bg-orange-200 text-orange-900 border-orange-400 ring-2 ring-orange-300 font-semibold'
                            : 'bg-gray-50 text-gray-400 border-gray-200 opacity-50 cursor-not-allowed'
                        }`}
                        title={activeScenario === 'C'
                          ? `Recargo por mora activo — después del ${formatDate(tuitionPricingWithTax.graceDeadline)}`
                          : `No aplica todavía — el periodo de mora inicia el ${formatDate(tuitionPricingWithTax.graceDeadline)}`}
                        disabled={activeScenario !== 'C'}
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Con Recargo: {formatCurrency(tuitionPricingWithTax.surchargeWithTax)}
                      </button>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Pronto pago hasta: {formatDate(tuitionPricingWithTax.dueDate)} · Gracia hasta: {formatDate(tuitionPricingWithTax.graceDeadline)} · Mora desde: {formatDate(tuitionPricingWithTax.graceDeadline)}
                    </p>
                  </div>
                ) : null
              )}

              {/* Monto a pagar */}
              <div className="space-y-1.5">
                <Label htmlFor="manualAmount">
                  Monto a Pagar <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-sm text-muted-foreground">$</span>
                  <Input
                    id="manualAmount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="0.00"
                    value={manualAmount}
                    onChange={(e) => setManualAmount(e.target.value)}
                    className="pl-7"
                  />
                </div>
                {/* Excedente sobre effectiveCap → saldo a favor notice */}
                {selectedInvoiceId && selectedInvoice && manualAmount && frontendEffectiveCap !== null && parseFloat(manualAmount) > frontendEffectiveCap + 0.01 && (
                  <div className="flex items-start gap-1.5 text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-2.5 py-2">
                    <Wallet className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>
                      El excedente de{' '}
                      <strong>{formatCurrency(parseFloat(manualAmount) - frontendEffectiveCap)}</strong>{' '}
                      se registrará automáticamente como <strong>Saldo a Favor</strong>.
                    </span>
                  </div>
                )}
                {!selectedInvoiceId && manualAmount && parseFloat(manualAmount) > 0 && (
                  <p className="text-xs text-blue-600">
                    El sistema pagará las cuentas de cobro comenzando por la más vencida hasta agotar el monto.
                  </p>
                )}
              </div>

              {/* Fecha de pago */}
              <div className="space-y-1.5">
                <Label htmlFor="fechaPago">Fecha de Pago <span className="text-red-500">*</span></Label>
                <Input id="fechaPago" type="date" value={fechaPago} max={getMexicoTodayDateOnly()} onChange={(e) => setFechaPago(e.target.value)} />
              </div>

              {/* Método de pago */}
              <div className="space-y-1.5">
                <Label>Método de Pago <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={availablePaymentMethods}
                  value={paymentMethod}
                  onChange={(val) => setPaymentMethod(val ? String(val) : null)}
                  placeholder="Selecciona método..."
                  className="w-full"
                />
              </div>

              {/* Serie de folio — editable aunque tenga default automático por forma de pago */}
              <div className="space-y-1.5">
                <SerieFolioSelector
                  availableSeries={availableSeries}
                  effectiveSerieId={effectiveSerieId}
                  autoSerieId={autoSerieId}
                  onChange={setManualSerieId}
                />
              </div>

              {/* Cuenta de destino */}
              <div className="space-y-1.5">
                <Label>Cuenta de Destino <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={(paymentAccounts ?? []).map(a => ({ value: a.id, label: `${a.code} — ${a.name}` }))}
                  value={bankAccountId ? parseInt(bankAccountId) : null}
                  onChange={(val) => setBankAccountId(val ? String(val) : null)}
                  placeholder="Selecciona cuenta..."
                  className="w-full"
                />
              </div>

              {/* Referencia */}
              <div className="space-y-1.5">
                <Label htmlFor="referencia">Referencia <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                <Input id="referencia" placeholder="Ej: SPEI-123456, Folio cheque..." value={referencia} onChange={(e) => setReferencia(e.target.value)} maxLength={100} />
              </div>

              {/* Notas */}
              <div className="space-y-1.5">
                <Label htmlFor="notes">Notas <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                <Input id="notes" placeholder="Observaciones adicionales..." value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>

              {/* Comprobante (imagen o PDF) */}
              <div className="space-y-1.5">
                <Label>Comprobante de Pago <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                {imagePreview === 'pdf' ? (
                  <div className="relative flex items-center gap-3 border rounded-lg px-4 py-3 bg-red-50">
                    <FileType2 className="h-8 w-8 text-red-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-red-700 truncate">{paymentImage?.name}</p>
                      <p className="text-xs text-red-500">{paymentImage ? (paymentImage.size / 1024).toFixed(1) + ' KB' : ''}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setPaymentImage(null); setImagePreview(null); }}
                      className="ml-auto bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : imagePreview ? (
                  <div className="relative inline-block w-full">
                    <img src={imagePreview} alt="Comprobante" className="w-full max-h-36 object-contain rounded border" />
                    <button
                      type="button"
                      onClick={() => { setPaymentImage(null); setImagePreview(null); }}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col items-center">
                      <ImageIcon className="h-6 w-6 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-500">Subir imagen o PDF del comprobante</span>
                    </div>
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleImageChange} />
                  </label>
                )}
              </div>

            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closePaymentDialog}>Cancelar</Button>
            <Button onClick={() => validateMutation.mutate()} disabled={validateMutation.isPending || !isFormValid}>
              {validateMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Registrando...</>
              ) : (
                <><CheckCircle className="mr-2 h-4 w-4" />Validar Pago</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk Validation Dialog ────────────────────────────── */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Validación Masiva de Pagos</DialogTitle>
            <DialogDescription>
              Sube un archivo CSV del banco con las columnas <strong>Referencia</strong> (7 dígitos), <strong>Concepto</strong>, <strong>Cantidad</strong> y <strong>Fecha</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="csv-file">Archivo CSV</Label>
              <Input id="csv-file" type="file" accept=".csv"
                onChange={(e) => { if (e.target.files?.[0]) setSelectedFile(e.target.files[0]); }} />
              {selectedFile && <p className="text-sm text-muted-foreground">Archivo: {selectedFile.name}</p>}
            </div>
            <div className="space-y-2">
              <Label>Cuenta bancaria destino</Label>
              <SearchableSelect
                options={(paymentAccounts ?? []).map(a => ({ value: a.id, label: `${a.code} — ${a.name}` }))}
                value={bulkBankAccountId ? parseInt(bulkBankAccountId) : null}
                onChange={(val) => setBulkBankAccountId(val ? String(val) : null)}
                placeholder="Seleccionar cuenta..."
              />
            </div>
            <div className="bg-muted p-4 rounded-lg space-y-3">
              <p className="text-sm font-medium">Formato mínimo requerido:</p>
              <pre className="text-xs bg-background p-3 rounded border font-mono leading-5">{
`Referencia,Concepto,Cantidad,Fecha
00010011,Pago enero,5000,2026-01-15
00130012,Colegiatura feb,4500.50,2026-02-10`
              }</pre>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-foreground">Reglas de aplicación:</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-none">
                  <li>📋 <strong>Referencia</strong>: 7 dígitos exactos — primeros 4 = número del alumno (ej. <code className="bg-background px-1 rounded">0001</code> = NUI-0001), últimos 3 = SKU del producto (ej. <code className="bg-background px-1 rounded">001</code>)</li>
                  <li>💬 <strong>Concepto</strong>: descripción o referencia del pago (se guarda como nota)</li>
                  <li>💰 <strong>Cantidad</strong>: monto total del pago en MXN</li>
                  <li>📅 <strong>Fecha</strong>: fecha del pago en formato YYYY-MM-DD</li>
                  <li>🏦 <strong>Método</strong>: siempre se registra como <em>Transferencia Bancaria</em></li>
                  <li>⬇️ <strong>Cascada</strong>: el dinero se aplica primero a la factura más antigua; si sobra, pasa automáticamente a la siguiente más antigua hasta agotar el monto</li>
                </ul>
              </div>
              <p className="text-xs text-muted-foreground border-t pt-2">
                * El CSV puede tener más columnas. El sistema buscará automáticamente las requeridas.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBulkDialog(false); setSelectedFile(null); setBulkBankAccountId(null); }}>Cancelar</Button>
            <Button
              onClick={() => selectedFile && bulkBankAccountId && bulkValidateMutation.mutate({ file: selectedFile, bankAccId: parseInt(bulkBankAccountId) })}
              disabled={!selectedFile || !bulkBankAccountId || bulkValidateMutation.isPending}
            >
              {bulkValidateMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</>
              ) : (
                <><Upload className="mr-2 h-4 w-4" />Validar Pagos</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
